import { type AddToCartHookProps } from "~/features/addToCart/hooks/useAddToCartForm";
import { useMemo } from "react";
import { SalesDataProvider } from "~/features/salesCampaign/hooks/useSalesData";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { resolveAddToCartScreen } from "~/features/addToCart/flow/resolveAddToCartScreen";
import type { CloseIntent } from "~/features/addToCart/flow/useAtcFlowController";
import {
  AtcWizardProviders,
  type AddToCartSubmitHandler,
  useAtcWizardProcess,
} from "~/features/addToCart/components/AddToCartProcess/AddToCartWizardShared";
import { AtcWizardScreens } from "~/features/addToCart/components/AddToCartProcess/AtcWizardScreens";

export interface AddToCartWizardProps extends AddToCartHookProps {
  restrictionQuery: string;
  onRequestClose: (intent: CloseIntent) => void;
  onSubmit: AddToCartSubmitHandler;
}

export const AddToCartWizard = ({ restrictionQuery, onRequestClose, onSubmit, ...hookProps }: AddToCartWizardProps) => {
  const process = useAtcWizardProcess({
    flowKind: "addToCart",
    hookProps,
    onRequestClose,
    onSubmit,
    resolveScreen: resolveAddToCartScreen,
    restrictionQuery,
  });

  const salesData = useMemo(
    () => calculateSalesCampaign4PDP({ productDescriptor: process.productDescriptor }),
    [process.productDescriptor],
  );

  return (
    <SalesDataProvider value={salesData.data}>
      <AtcWizardProviders
        derivedCtx={process.derivedCtx}
        flowContextProps={process.flowContextProps}
        form={process.form}
      >
        <AtcWizardScreens
          canSeeAddToCartDisclaimer={process.canSeeAddToCartDisclaimer}
          canSubmitToCartOrQuote={process.canSubmitToCartOrQuote}
          flowKind="addToCart"
          isSending={process.isSending}
          productDescriptor={process.productDescriptor}
          screen={process.currentScreen}
          submit={process.submit}
        />
      </AtcWizardProviders>
    </SalesDataProvider>
  );
};
